import API from "../api/manager";

// The Automation API (hashh-api-v2 routes/automation.ts), as the app's
// app/automation/AutomationService.js calls it. Requests keep the HTTP status,
// because the screens need it: 404 on capabilities means the feature is off for
// the home, and 409 with `confirm` means a lock, gate, alarm or breaker must be
// confirmed first.

const FALLBACK = "Something went wrong. Please try again.";
const OFFLINE = "Couldn't reach HASHh. Check the internet connection.";

// What a failed request rejects with: the API's message, its status, and for a
// 409 the devices to confirm ([{ applianceId, name, deviceType }]).
export class AutomationError extends Error {
  constructor({ status = 0, message, field = null, confirm = null }) {
    super(message || FALLBACK);
    this.status = status;
    this.field = field;
    this.confirm = confirm;
  }
}

const request = async (method, url, data, params) => {
  try {
    return await API({ method, url, data, params }, true, { keepStatus: true });
  } catch (error) {
    if (error && typeof error === "object" && "status" in error) {
      const body = error.data && typeof error.data === "object" ? error.data : {};
      throw new AutomationError({
        status: error.status,
        message: typeof body.message === "string" ? body.message : null,
        field: body.field ?? null,
        confirm: Array.isArray(body.confirm) ? body.confirm : null,
      });
    }
    // No answer from the server, or the login ran out (manager.js signs in again).
    throw new AutomationError({ message: typeof error === "string" && /login has expired/i.test(error) ? error : OFFLINE });
  }
};

const base = (homeId) => `automation/${homeId}`;
const paging = ({ limit, before } = {}) => ({ ...(limit ? { limit } : {}), ...(before ? { before } : {}) });

export const AutomationService = {
  // The home's time zone and location, which types it uses, and what each
  // device supports. null when the feature is off for the home (404).
  async capabilities(homeId) {
    try {
      return await request("get", `${base(homeId)}/capabilities`);
    } catch (error) {
      if (error.status === 404) return null;
      throw error;
    }
  },

  // The home's automations (of one type, if given), or one device's (which
  // also lists what the device does by itself, as read-only entries).
  list: (homeId, { type, applianceId } = {}) => {
    const params = { ...(type ? { type } : {}), ...(applianceId ? { applianceId } : {}) };
    return request("get", base(homeId), undefined, Object.keys(params).length ? params : undefined);
  },

  get: (homeId, id) => request("get", `${base(homeId)}/${id}`),

  // The app's create and update, without its Child Lock check (a phone
  // setting). A 400 names the `field` that's wrong; a 409 with `confirm` is
  // sent again with confirmed: true.
  create: (homeId, automation) => request("post", base(homeId), automation),

  // fields left out keep their value
  update: (homeId, id, changes) => request("put", `${base(homeId)}/${id}`, changes),

  // { timezone?, location?: { lat, lon } }; owners only. Answers the home's
  // { timezone, timezoneSet, location }.
  setHome: (homeId, home) => request("put", `${base(homeId)}/home`, home),

  remove: (homeId, id) => request("delete", `${base(homeId)}/${id}`),

  // Answers the automation as it now is.
  setEnabled: (homeId, id, enabled) => request("put", `${base(homeId)}/${id}/enabled`, { enabled }),

  // Runs it now, like tapping its devices:
  // { runId, status: ok | failed | skipped, message, devices: [{ applianceId, ok, message }] }.
  run: (homeId, id, confirmed) => request("post", `${base(homeId)}/${id}/run`, confirmed ? { confirmed: true } : {}),

  // { runs, next }: pass next as `before` for the page after.
  runs: (homeId, id, page) => request("get", `${base(homeId)}/${id}/runs`, undefined, paging(page)),

  history: (homeId, page) => request("get", `${base(homeId)}/history`, undefined, paging(page)),

  // NFC tags, owners only: [{ id, name, automationId, automationName, url,
  // createdBy, created_at, lastUsedAt }], newest first, revoked ones left
  // out. Tags are written with a phone in the app.
  tags: (homeId, id) => request("get", `${base(homeId)}/${id}/tags`),
  // a revoked tag stops working at once: { id, revoked: true }
  revokeTag: (homeId, tagId) => request("delete", `${base(homeId)}/tags/${tagId}`),
};
